import TextField from '@material-ui/core/TextField';
import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { SproutButton } from 'components/Button/SproutButton';
import { TextV2 } from 'components/TextV2/TextV2';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';
import styles from './MobilePhone.module.scss';
import { Redirect, useHistory } from 'react-router-dom';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { CheckoutRoutePath } from '../Checkout';
import { FooterLinks } from '../CheckoutEmailAndInfoPage/FooterLinks/FooterLinks';
import { FormWrapper } from 'components/Form/FormWrapper';
import FormControl from '@material-ui/core/FormControl';
import { SproutBanner } from 'components/SproutBanner/SproutBanner';
import { makeMiscInfoJsonString } from 'services/analytics/analytics';
import { defaultErrorState, generateCartRequestObject } from '../Utils';
import { acquiringFormNameMap, FormEndpointType } from 'components/Contact/FormEndpoints';
import { datadogRum } from '@datadog/browser-rum';
import { useCheckout } from '../CheckoutProvider';
import { useCookieConfig } from 'hooks/useCookieConfig';
import { useReseller } from 'hooks/Reseller/useReseller';
import { generateLead, getOtp } from 'services/shop/checkout';
import { isAccountOnlyCart } from 'components/Cart/utils/getCartProductsAndAccessoriesUtils';
import { MOBILE_PHONE_TEST_ID } from './MobilePhone.const';
import { useCartContext } from 'components/Cart/hooks/useCartContext';
import { useFiMerchant } from 'hooks/useFiMerchant';

interface IMobilePhoneFields {
  phoneNumber: string;
}

interface IMobilePhoneProps {
  isMaxOtpRequests: boolean;
  setIsMaxOtpRequests: (value: boolean) => void;
  setPhoneNumberChangeAttempts: (updateFn: (value: number) => number) => void;
}

export const MobilePhone: FC<IMobilePhoneProps> = ({
  isMaxOtpRequests,
  setIsMaxOtpRequests,
  setPhoneNumberChangeAttempts,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { resellerId } = useReseller();
  const cookieConfig = useCookieConfig();
  const { cart, checkout } = useCartContext();
  const { subscriberRefId, merchantProspectData } = useFiMerchant();
  const {
    selectedPurchaseType,
    setIsLoading,
    setErrorState,
    setLeadId,
    setUserInfo,
    userInfo,
    landlinePhoneNumber,
    setLandlinePhoneNumber,
    setHasClaimedOtpAccount,
  } = useCheckout();

  const isNoHardware = isAccountOnlyCart(cart);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IMobilePhoneFields>({
    mode: 'onSubmit',
    criteriaMode: 'firstError',
    defaultValues: {
      phoneNumber: subscriberRefId ? merchantProspectData?.mobilePhoneNumber : userInfo?.phoneNumber,
    },
  });

  if (!userInfo) {
    return <Redirect to={redesignV2RoutePath.CART} />;
  }

  const validatePhoneNumber = (value: string | undefined) => {
    if (value?.replace(/\D/g, '') === landlinePhoneNumber) return t('otp.phone.errors.mobile-validation');
  };

  const onSubmit = async (values: IMobilePhoneFields) => {
    const { phoneNumber } = values;
    const phoneNumberWithoutFormat = phoneNumber.replace(/\D/g, '');
    const emailAddress = userInfo.emailAddress;
    const userInfoWithPhoneNumber = { ...userInfo, phoneNumber: phoneNumberWithoutFormat };

    setUserInfo({ ...userInfoWithPhoneNumber });

    try {
      setIsLoading(true);
      const marketingInfo = JSON.parse(makeMiscInfoJsonString(cookieConfig));

      if (!resellerId) throw new Error('Cannot submit cart. Missing: Reseller ID');

      const cartRequestObject = generateCartRequestObject({
        cart,
        checkout,
        userInfo: userInfoWithPhoneNumber,
        emailAddress,
        marketingInfo: {
          ...marketingInfo,
          acquiring_form:
            acquiringFormNameMap[
              isNoHardware ? FormEndpointType.CLOVER_NO_HARDWARE : FormEndpointType.CLOVER_HARDWARE
            ],
        },
        resellerId,
        selectedPurchaseType,
        subscriberRefId,
      });

      const leadResponse = await generateLead(cartRequestObject);
      const leadId = leadResponse.data.uuid;
      setLeadId(leadId);

      if (leadResponse.data?.accountUuid) {
        setHasClaimedOtpAccount(true);
        history.push(redesignV2RoutePath.CART);
        return;
      }

      await getOtp(leadId);
      setIsMaxOtpRequests(false);
      setPhoneNumberChangeAttempts(prev => prev + 1);

      history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.PASSCODE);
    } catch (e) {
      const message = e.response?.data?.message || e.message || 'Unknown error';
      datadogRum.addError(e);

      if (message.includes('must be wireless')) {
        setLandlinePhoneNumber(phoneNumberWithoutFormat);
        return;
      }

      const leadError = message.includes('Cannot modify immutable lead with UUID:');
      const duplicateAccountError = message.includes('already created account in COS');
      const apiError = !duplicateAccountError && !leadError;
      setErrorState({
        ...defaultErrorState,
        hasApiError: apiError,
        hasDuplicateLeadError: leadError,
        hasDuplicateAccountError: duplicateAccountError,
      });

      if (!apiError) {
        history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.OTP_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <section className={styles.otpContainer}>
      <header className={styles.header}>
        <TextV2 tagName="h2" variant="h2-bold" value={t('otp.phone.title')} />
        <TextV2 className={styles.subtitle} tagName="h5" variant="body" value={t('otp.phone.subtitle')} />
        {(isMaxOtpRequests || landlinePhoneNumber) && (
          <SproutBanner
            bannerText={
              isMaxOtpRequests ? t('otp.phone.errors.out-of-otp-requests') : t('otp.phone.errors.landline')
            }
            variant="error"
          />
        )}
      </header>
      <div className={styles.requiredFieldWrapper}>
        <TextV2 tagName="p" variant="body-small" value={t('otp.phone.required-field')} />
      </div>
      <section className={styles.formContainer}>
        <FormWrapper>
          <FormControl fullWidth>
            <form id="mobile-form" onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <TextV2
                  className={styles.formHeading}
                  tagName="h5"
                  variant="h5-medium"
                  value={t('otp.phone.form-heading')}
                />
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: t('otp.phone.errors.required'),
                    pattern: {
                      value: /[2-9](.*\d.*){9,}/,
                      message: t('otp.phone.errors.invalid'),
                    },
                    validate: validatePhoneNumber,
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <PatternFormat
                      {...field}
                      id="mobile-phone-number"
                      data-testid={MOBILE_PHONE_TEST_ID.phoneNumberInput}
                      customInput={TextField}
                      type="tel"
                      autoComplete="tel"
                      label={t('otp.phone.label')}
                      format="(###) ###-####"
                      variant="outlined"
                      getInputRef={ref}
                      helperText={errors?.phoneNumber?.message}
                      error={!!errors?.phoneNumber}
                      autoFocus
                      fullWidth
                    />
                  )}
                />
              </div>
            </form>
          </FormControl>
        </FormWrapper>
      </section>
      <TextV2
        className={styles.phoneDisclaimer}
        tagName="p"
        variant="body-small"
        value={t('otp.phone.disclaimer')}
      />
      <SproutButton
        className={styles.continueButton}
        type="submit"
        variant={LinkVariant.PRIMARY}
        form="mobile-form"
        data-testid={MOBILE_PHONE_TEST_ID.continueButton}
      >
        {t('continue')}
      </SproutButton>
      <FooterLinks />
    </section>
  );
};
